import * as React from 'react'

import {
  Button,
  CardActionArea,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import MuiCard from '@mui/material/Card'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSession } from 'next-auth/react'
import { useRouter } from 'nextjs-toploader/app'

import { ContactButton } from '@/components/ContactButton/ContactButton'
import { AddToFavorites } from '@/components/Favorite/FavoriteListActions/AddToFavorites'
import { ProductCardSkeleton } from '@/components/ProductCard/ProductCard.skeleton'
import { ProductLabels } from '@/components/ProductPrice/ProductLabels'
import { ProductMinSaveLabels } from '@/components/ProductPrice/ProductMinSaveLabels'
import { ProductPrice } from '@/components/ProductPrice/ProductPrice'
import { ProductSaveLabels } from '@/components/ProductPrice/ProductSaveLabels'
import { ProductPartStatusAndStock } from '@/components/ProductStock/ProductPartStatusAndStock'
import { QuantityCounter } from '@/components/QuantityCounter/QuantityCounter'
import { SignUpButton } from '@/components/SignUpButton/SignUpButton'
import { getPriceMarkers } from '@/helpers/getPriceMarkers'
import { getWebShopStatusVisibility } from '@/helpers/getWebShopStatusVisibility'
import { getProductThumbnail } from '@/helpers/productImages'
import { useProductPrice } from '@/hooks/bff/prices/getPrices/useProductPrice'
import { useAddToCartState } from '@/hooks/useAddToCartState'
import { useCart } from '@/hooks/useCart'
import { useCartQuantityChange } from '@/hooks/useCartQuantityChange'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import { useFullProductQuery } from '@/hooks/useFullProductQuery'
import { useInViewport } from '@/hooks/useInViewport'
import { useItemListImpression } from '@/hooks/useItemListImpression'
import { useItemListSelection } from '@/hooks/useItemListSelection'
import { useSalesUnitDescription } from '@/hooks/useSalesUnitDescription'
import { useTenant } from '@/hooks/useTenant'
import { useTranslations } from '@/hooks/useTranslations'
import { useProductListTracking } from '@/providers/ProductListTrackingProvider'

import type { SxProps, Theme } from '@mui/material'
import type { ProductProps } from '@/interfaces/product.interface'
import type { CartLineItem } from '@/lib/eventBus/trackingEvents'

interface Props {
  productCode: string
  product?: ProductProps
  onClick?: VoidFunction
  sx?: SxProps<Theme>
  favoriteListsIds?: string[]
  /** False for CMS-curated lists, which can name products that no longer exist. */
  shouldRetryProductLoad?: boolean
}

interface ProductCardBuyButtonProps {
  productProps?: ProductProps
  allowedQuantity: number
  quantity: number
  isDisabled: boolean
  translate: ReturnType<typeof useTranslations>['translate']
  onQuantityChange: (value: number) => void
  onAddToCart: VoidFunction
}

const ProductCardBuyButton: React.FC<ProductCardBuyButtonProps> = ({
  productProps,
  allowedQuantity,
  quantity,
  isDisabled,
  translate,
  onQuantityChange,
  onAddToCart,
}) => {
  if (!productProps) {
    return null
  }

  return (
    <Stack direction="row" gap={1} flex={1}>
      <QuantityCounter max={allowedQuantity} min={0} value={quantity} updateCounter={onQuantityChange} />
      <Button
        data-testid="productCard-buyButton"
        sx={{ flex: 1 }}
        variant="contained"
        size="medium"
        fullWidth
        onClick={onAddToCart}
        disabled={isDisabled}
      >
        {translate('product.add')}
      </Button>
    </Stack>
  )
}

interface ProductCardInfoProps {
  productCode: string
  translate: ReturnType<typeof useTranslations>['translate']
  showSailabCode: boolean
  productProps?: ProductProps
  salesUnitDescription: string
  product?: ProductProps
  minSaveLabel?: string | null
  priceData?: CartLineItem['priceData']
  shouldShowPrice: boolean
  quantity: number
}

const ProductCardInfo: React.FC<ProductCardInfoProps> = ({
  productCode,
  translate,
  showSailabCode,
  productProps,
  salesUnitDescription,
  product,
  minSaveLabel,
  priceData,
  shouldShowPrice,
  quantity,
}) => (
  <CardContent component={Stack} flexGrow={1} width="100%">
    <Typography variant="productNameLong" data-testid="productCard-name">
      {productProps?.name ?? translate('product.missing')}
    </Typography>
    <Typography variant="description">
      {translate('product.code')}: {productProps?.code ?? productCode}
    </Typography>
    {showSailabCode && productProps?.sailabCode && (
      <Typography variant="description">
        {translate('product.sailabCode')}: {productProps.sailabCode}
      </Typography>
    )}
    {productProps && (
      <Typography variant="description">
        {translate('product.salesUnit')}: {salesUnitDescription}
      </Typography>
    )}
    <ProductPartStatusAndStock product={product ?? productProps} />

    {minSaveLabel && priceData?.minimumQuantity != null && (
      <ProductMinSaveLabels
        sx={{ textAlign: 'right' }}
        minSaveLabel={minSaveLabel}
        minimumQuantity={priceData.minimumQuantity}
        percentage={priceData.discountPercentage}
      />
    )}
    {shouldShowPrice && (
      <ProductPrice
        product={productProps}
        quantity={quantity}
        priceCard
        sx={{ marginTop: 'auto' }}
        data-testid="productCard-price"
      />
    )}
  </CardContent>
)

interface ProductCardFooterActionsProps {
  shouldShowBuyButton: boolean
  shouldShowContactButton: boolean
  shouldShowSignUpButton: boolean
  buyButtonProps: ProductCardBuyButtonProps
}

const ProductCardFooterActions: React.FC<ProductCardFooterActionsProps> = ({
  shouldShowBuyButton,
  shouldShowContactButton,
  shouldShowSignUpButton,
  buyButtonProps,
}) => (
  <>
    {shouldShowBuyButton && <ProductCardBuyButton {...buyButtonProps} />}
    {shouldShowContactButton && <ContactButton />}
    {shouldShowSignUpButton && <SignUpButton />}
  </>
)

export const ProductCard: React.FC<Props> = ({
  productCode,
  product,
  onClick,
  sx,
  favoriteListsIds,
  shouldRetryProductLoad = true,
}) => {
  const { translate } = useTranslations()
  const cartOperations = useCart()
  const { cart } = cartOperations
  const { addToCart } = useCartQuantityChange(cartOperations)
  const queryClient = useQueryClient()
  const [quantity, setQuantity] = React.useState(1)
  const [updateProductData, setUpdateProductData] = React.useState(true)
  const { data: session } = useSession()
  const theme = useTheme()
  const { push } = useRouter()
  const tenant = useTenant()
  const showSailabCode = useFeatureFlag('sailabCode')
  const [viewportRef, inView] = useInViewport<HTMLDivElement>()
  const listTracking = useProductListTracking()

  // Subscribe to this product's cached price without triggering a fetch — the child <ProductPrice>
  // lazy-fetches it when the card scrolls into view, and this re-renders reactively when it lands.
  const { priceData, isLoading: isPriceLoading } = useProductPrice(productCode, quantity, { enabled: false })

  const { queryKey: productCacheKey, queryFn: fetchProduct } = useFullProductQuery()(productCode)

  if (updateProductData && product) {
    queryClient.setQueryData(productCacheKey, product)
    setUpdateProductData(false)
  }

  const shouldFetchProduct = inView || product != null

  const { data: productProps, isLoading } = useQuery({
    queryKey: productCacheKey,
    queryFn: fetchProduct,
    enabled: shouldFetchProduct,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retry: shouldRetryProductLoad ? undefined : 0,
    staleTime: Number.POSITIVE_INFINITY,
  })
  const salesUnitDescription = useSalesUnitDescription(productProps)

  const { shouldShowBuyButton, shouldShowContactButton, shouldShowPrice, shouldShowSignUpButton } =
    getWebShopStatusVisibility(productProps?.productWebshopStatus, productProps?.productPartStatus, session != null)

  const impressionRef = useItemListImpression<HTMLDivElement>({
    product: productProps,
    priceData,
    isPriceShown: shouldShowPrice,
    isPriceLoading,
  })
  const trackItemSelected = useItemListSelection(productProps, priceData)

  const { allowedQuantity, isDisabled } = useAddToCartState({
    productCode,
    quantity,
    productStock: productProps?.productStock,
    productPartStatus: productProps?.productPartStatus,
  })

  const handleAddToCart = React.useCallback(async () => {
    try {
      await addToCart({
        product: productProps,
        productCode,
        quantity,
        listContext: listTracking?.listContext,
        index: listTracking?.getItemIndex(productCode),
      })
    } catch {
      /* empty */
    }
  }, [addToCart, productProps, productCode, quantity, listTracking])

  const image = getProductThumbnail(productProps?.images)
  const pdpLink = product?.url ?? productProps?.url ?? `/p/${productCode}`

  const handleClick = () => {
    trackItemSelected()
    push(pdpLink)
    onClick?.()
  }

  React.useEffect(() => {
    const cartProduct = cart?.entries?.find((entry) => entry.product?.code === productCode)
    setQuantity(cartProduct?.quantity || 1)
  }, [cart?.entries, productCode])

  if (!shouldFetchProduct || isLoading) {
    return <ProductCardSkeleton ref={viewportRef} />
  }

  const { labelText, labelStyleClass, saveLabel, minSaveLabel } = getPriceMarkers(
    priceData ?? null,
    tenant.currency,
    tenant.locale
  )

  return (
    <MuiCard
      ref={impressionRef}
      component={Stack}
      variant="outlined"
      data-testid="productCard-card"
      sx={{
        height: '100%',
        maxWidth: theme.customDimensions.productCardWidth,
        background: productProps ? 'none' : theme.palette.border.default,
        opacity: productProps ? 1 : 0.5,
        '&:hover': { cursor: 'auto' },
        ...sx,
      }}
    >
      <CardHeader
        sx={{ paddingBottom: 0 }}
        avatar={<ProductLabels labelText={labelText} colorClassName={labelStyleClass} />}
        action={
          session != null && (
            <AddToFavorites
              productCode={productCode}
              disabled={!productProps}
              isInFavoriteList={(favoriteListsIds?.length ?? 0) > 0}
            />
          )
        }
      />
      <CardActionArea
        disabled={!productProps}
        draggable="false"
        onClick={handleClick}
        disableRipple
        disableTouchRipple
        sx={{
          ':hover .MuiCardActionArea-focusHighlight': { opacity: 0 },
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
        }}
      >
        <CardMedia
          loading="lazy"
          component="img"
          src={image?.url ?? '/placeholder.png'}
          alt={image?.altText}
          sx={{
            objectFit: 'contain',
            padding: theme.spacing(2),
            height: theme.customDimensions.productCardImageHeight,
          }}
          onError={({ currentTarget }) => {
            currentTarget.onerror = null
            currentTarget.src = '/placeholder.png'
          }}
        />
        {saveLabel && (
          <ProductSaveLabels percentage={priceData?.discountPercentage} saveLabel={saveLabel} productCart />
        )}
        <ProductCardInfo
          productCode={productCode}
          translate={translate}
          showSailabCode={showSailabCode}
          productProps={productProps}
          salesUnitDescription={salesUnitDescription}
          product={product}
          minSaveLabel={minSaveLabel}
          priceData={priceData}
          shouldShowPrice={shouldShowPrice}
          quantity={quantity}
        />
      </CardActionArea>
      <CardActions sx={{ padding: theme.spacing(2), paddingTop: 0 }}>
        <ProductCardFooterActions
          shouldShowBuyButton={shouldShowBuyButton}
          shouldShowContactButton={shouldShowContactButton}
          shouldShowSignUpButton={shouldShowSignUpButton}
          buyButtonProps={{
            productProps,
            allowedQuantity,
            quantity,
            isDisabled,
            translate,
            onQuantityChange: setQuantity,
            onAddToCart: handleAddToCart,
          }}
        />
      </CardActions>
    </MuiCard>
  )
}
